import { AlertTriangle } from 'lucide-react'
import { cn } from '@/shared/utils/cn'
import { Button } from './button'

interface PendingInvoiceAlertProps {
  onPay: () => void
  className?: string
  /** Texto del CTA. Default: "Pagar ahora". */
  ctaLabel?: string
  /** Override del título. Default: "Factura pendiente de pago". */
  title?: string
  /** Override del cuerpo. Default texto genérico. */
  description?: string
}

/**
 * Banner presentacional de factura pendiente. No conoce queries — sólo recibe
 * el callback de pago. Pensado para montarse arriba del contenido principal
 * cuando `canCreateBusiness === false`.
 *
 * Estilo basado en el `.alert-banner` del prototipo (`tecopos_prototipo.html`):
 * fondo ámbar suave, ícono en cuadrado naranja, CTA destacado a la derecha.
 */
export function PendingInvoiceAlert({
  onPay,
  className,
  ctaLabel = 'Pagar ahora',
  title = 'Factura pendiente de pago',
  description = 'Tienes una factura pendiente. Paga ahora para activar tus servicios y poder crear nuevos negocios.',
}: PendingInvoiceAlertProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-start gap-3 rounded-card border border-warning/40 bg-warning-soft p-4',
        className,
      )}
      role="alert"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-warning text-white">
        <AlertTriangle className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-ink">{title}</p>
        <p className="mt-0.5 text-sm text-ink-muted">{description}</p>
      </div>
      <Button
        onClick={onPay}
        variant="primary"
        className="bg-warning text-white hover:bg-warning/90"
      >
        {ctaLabel}
      </Button>
    </div>
  )
}
